import { WINE_COLOUR } from "@proef/domain";
import { z } from "zod";

const text = z.string().trim().min(1).max(200);
const country = z.string().length(2).describe("ISO 3166-1 alpha-2");
const vintage = z.number().int().min(1800).max(2100).nullable().optional();

/** Max body of `POST /v1/submissions`, in bytes. */
export const SUBMISSION_MAX_BYTES = 4096;

/** A producer the on-device catalogue does not know. */
export const SubmissionProducer = z.object({
  kind: z.literal("producer"),
  name: text,
  country: country.optional(),
});

/** A wine the on-device catalogue does not know, under a producer it may or may not know. */
export const SubmissionWine = z.object({
  kind: z.literal("wine"),
  producer: text,
  name: text,
  vintage,
  country: country.optional(),
  colour: z.enum(WINE_COLOUR).optional(),
  sparkling: z.boolean().default(false),
  grapes: z.array(z.string().trim().min(1).max(100)).max(30).default([]),
  /**
   * The device's own id for this wine when it is edge-only (`manual:...`), so the edge can move that
   * device's tastings and cellar to the catalogue wine once druif imports it. Kept per device, never on
   * the shared submission.
   */
  manualWineId: z.string().min(1).max(200).optional(),
});

/** A GTIN scanned off a wine the catalogue has, but not under that code. */
export const SubmissionBarcode = z.object({
  kind: z.literal("barcode"),
  /** As scanned; the edge checks the digits and normalises to GTIN-14. */
  gtin: z.string().regex(/^[0-9]{8,14}$/),
  wine: z.object({ producer: text, name: text, vintage }),
  /** The catalogue's own id for the wine, when the device picked it from the local catalogue. */
  catalogueWineId: z.string().min(1).max(200).optional(),
});

/**
 * `POST /v1/submissions`: something a device proposes to add to the catalogue, queued for verification.
 * Unknown fields are dropped, so a device id or a note cannot ride along into storage.
 */
export const NewSubmission = z.discriminatedUnion("kind", [
  SubmissionProducer,
  SubmissionWine,
  SubmissionBarcode,
]);
export type NewSubmission = z.infer<typeof NewSubmission>;

/** `received` also answers a submission already on file: the phone cannot tell the two apart, and need not. */
export const SubmissionResponse = z.object({ status: z.literal("received") });
export type SubmissionResponse = z.infer<typeof SubmissionResponse>;

/** pending: awaiting review; verified: a human accepted it; imported: druif holds it; rejected: declined. */
export const SUBMISSION_STATUS = ["pending", "verified", "imported", "rejected"] as const;
export const SubmissionStatus = z.enum(SUBMISSION_STATUS);
export type SubmissionStatus = z.infer<typeof SubmissionStatus>;

/** One queued submission as the admin list returns it; `submissions` counts the devices that proposed it. */
export const SubmissionItem = z.object({
  id: z.number().int().positive(),
  kind: z.enum(["producer", "wine", "barcode"]),
  payload: NewSubmission,
  submissions: z.number().int().positive(),
  status: SubmissionStatus,
  createdAt: z.iso.datetime(),
});
export type SubmissionItem = z.infer<typeof SubmissionItem>;

/** `GET /v1/submissions/pending` (admin): a page and where the next one starts, `null` at the end. */
export const SubmissionList = z.object({
  items: z.array(SubmissionItem),
  nextCursor: z.string().nullable(),
});
export type SubmissionList = z.infer<typeof SubmissionList>;

/** Most ids one review call may carry. */
export const SUBMISSION_REVIEW_MAX_IDS = 100;

/** Most named wines one result may carry: one winery's range per review call, matched to the devices' own wines. */
export const SUBMISSION_RESULT_MAX_NAMED_WINES = 100;

/** A wine druif imported, in the `druif:<n>` form devices keep; name and vintage let the edge match a device's own wine. */
export const SubmissionResultWine = z.object({
  id: z.string().regex(/^druif:[1-9][0-9]{0,11}$/),
  name: z.string().trim().min(1).max(200),
  vintage: z.number().int().min(1800).max(2100).nullable().optional(),
});
export type SubmissionResultWine = z.infer<typeof SubmissionResultWine>;

/** Most wine ids one result may carry: a winery's whole range, kept small enough for the 8 KiB column. */
export const SUBMISSION_RESULT_MAX_WINES = 300;

/**
 * What druif found for a submission, sent with the review. Ids are druif's own (a wine's `druif:<n>` form on the
 * device); the producer's name is what the device's notice shows. Unknown fields are dropped.
 */
export const SubmissionResult = z.object({
  producer: z
    .object({ id: z.number().int().positive(), name: z.string().trim().min(1).max(200) })
    .optional(),
  wineIds: z.array(z.number().int().positive()).max(SUBMISSION_RESULT_MAX_WINES).optional(),
  wineCount: z.number().int().nonnegative().max(100_000).optional(),
  wines: z.array(SubmissionResultWine).max(SUBMISSION_RESULT_MAX_NAMED_WINES).optional(),
});
export type SubmissionResult = z.infer<typeof SubmissionResult>;

/** `POST /v1/submissions/review` (admin): moves submissions out of `pending` once a human decided or druif imported them. */
export const SubmissionReviewRequest = z.object({
  ids: z.array(z.number().int().positive()).min(1).max(SUBMISSION_REVIEW_MAX_IDS),
  status: z.enum(["verified", "imported", "rejected"]),
  note: z.string().trim().max(500).optional(),
  result: SubmissionResult.optional(),
});
export type SubmissionReviewRequest = z.infer<typeof SubmissionReviewRequest>;

export const SubmissionReviewResponse = z.object({ updated: z.number().int().nonnegative() });
export type SubmissionReviewResponse = z.infer<typeof SubmissionReviewResponse>;

/** One of a device's own submissions: where it stands, and for the notice what druif made of it. */
export const SubmissionNotice = z.object({
  id: z.number().int().positive(),
  kind: z.enum(["producer", "wine", "barcode"]),
  status: SubmissionStatus,
  note: z.string().optional(),
  result: SubmissionResult.optional(),
  createdAt: z.iso.datetime(),
  /** When the review last moved it; the creation time while it is pending. */
  updatedAt: z.iso.datetime(),
  /** Whether this device has marked the notice seen. */
  seen: z.boolean(),
  /** Whether the edge has moved this device's own wine (tastings, cellar) to the catalogue wine. */
  relinked: z.boolean(),
  /** The catalogue wine (`druif:<n>`) the device's wine became, once `relinked`. */
  wineId: z.string().optional(),
});
export type SubmissionNotice = z.infer<typeof SubmissionNotice>;

/** `GET /v1/submissions/mine`: the device's last 100 submissions, newest first. */
export const SubmissionMineList = z.object({ items: z.array(SubmissionNotice) });
export type SubmissionMineList = z.infer<typeof SubmissionMineList>;

/** `POST /v1/submissions/mine/seen`: the notices the device has shown. */
export const SubmissionSeenRequest = z.object({
  ids: z.array(z.number().int().positive()).min(1).max(SUBMISSION_REVIEW_MAX_IDS),
});
export type SubmissionSeenRequest = z.infer<typeof SubmissionSeenRequest>;

export const SubmissionSeenResponse = z.object({ updated: z.number().int().nonnegative() });
export type SubmissionSeenResponse = z.infer<typeof SubmissionSeenResponse>;
